import { memo as Memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { gsap } from 'gsap';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';

//* Components
import Text from '../Text';

//* Styles
import FilterStyle from './style';

const Filter = Memo(({ data, parentCallback, ...props }) => {
	//! States
	const [activeIndex, setActiveIndex] = useState(0);

	//! Refs
	let ref = useRef([]);
	const filterRef = useRef([]);
	const scrollWrapRef = useRef(null);
	const suppressChipClickRef = useRef(false);
	const tl = useMemo(() => gsap.timeline({ pause: true }), []);
	const tl2 = useMemo(() => gsap.timeline({ pause: true }), []);

	const handelActiveClass = (x) => {
		setActiveIndex(x);
	};

	useEffect(() => {
		tl2.to(
			filterRef.current,
			0,
			{
				opacity: 1,
				delay: 0.9,
			},
			0.15
		);
	}, [props.winWidth]);

	const singleItemType = useCallback(
		(itemType, i) => {
			return (
				<div
					key={i}
					ref={(el) => (filterRef.current[i] = el)}
					className={`filtersItem ${i === activeIndex ? 'active' : ''}`}
					onClick={() => {
						if (suppressChipClickRef.current) {
							suppressChipClickRef.current = false;
							return;
						}
						parentCallback(itemType);
						handelActiveClass(i);
					}}>
					<Text className='p-m font-poppins font-medium'>
						<Text
							tag={'span'}
							className={`textVisible p-m font-poppins font-medium`}
							ref={(el) => (ref.current[i] = el)}>
							{itemType}
						</Text>
						<Text
							tag={'span'}
							className={'textHide'}>
							{itemType}
						</Text>
					</Text>
				</div>
			);
		},
		[parentCallback]
	);

	useEffect(() => {
		tl.staggerTo(filterRef.current, 0, { opacity: 1, delay: 0.9 }, 0.15);
	}, [props.winWidth]);

	useEffect(() => {
		const el = scrollWrapRef.current;
		if (!el) return;

		const TH = 8;
		let startX = 0;
		let startScroll = 0;
		let down = false;
		let dragged = false;

		function offWin() {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
			window.removeEventListener('blur', onUp);
			document.body.style.userSelect = '';
			el.classList.remove('isDragging');
		}

		function onUp() {
			if (!down) return;
			const didDrag = dragged;
			down = false;
			dragged = false;
			if (didDrag) suppressChipClickRef.current = true;
			offWin();
		}

		function onMove(e) {
			if (!down) return;
			const dx = e.clientX - startX;
			if (!dragged && Math.abs(dx) > TH) {
				dragged = true;
				el.classList.add('isDragging');
				document.body.style.userSelect = 'none';
			}
			if (dragged) {
				el.scrollLeft = startScroll - dx;
				e.preventDefault();
			}
		}

		function onDown(e) {
			if (e.button !== 0) return;
			if (el.scrollWidth <= el.clientWidth + 2) return;

			down = true;
			dragged = false;
			startX = e.clientX;
			startScroll = el.scrollLeft;

			window.addEventListener('mousemove', onMove, { passive: false });
			window.addEventListener('mouseup', onUp);
			window.addEventListener('blur', onUp);
		}

		el.addEventListener('mousedown', onDown);

		return () => {
			el.removeEventListener('mousedown', onDown);
			offWin();
		};
	}, [props.winWidth, data]);

	//! Items filter
	const filtersItem = useMemo(() => {
		let includesResult = ['all'];
		let result = [];

		for (let str of data) {
			if (!includesResult.includes(str)) {
				includesResult.push(str);
			}
		}

		for (let i = 0; i < includesResult.length; i++) {
			result.push(singleItemType(includesResult[i], i));
		}

		return result;
	}, [data, handelActiveClass]);

	return (
		<FilterStyle className={'filterCont'}>
			<div
				className={'filterScrollWrap'}
				ref={scrollWrapRef}>
				<div className={'filterRow'}>
					<div className={'filterWrap'}>{filtersItem}</div>
				</div>
			</div>
		</FilterStyle>
	);
});

export default withUIContext(Filter, ['winWidth']);
